import React from 'react';
import { CheckCircle, Loader2, AlertCircle, Upload, FileSearch, Sparkles, Save } from 'lucide-react';

const UploadProgress = ({ progress, currentStep, status = 'processing' }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-red-600" />;
      case 'processing':
      default:
        return <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-brown-600 animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-600';
      case 'failed':
        return 'bg-red-600';
      case 'processing':
      default:
        return 'bg-brown-600';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'completed':
        return 'Complete!';
      case 'failed':
        return 'Failed';
      case 'processing':
      default:
        return currentStep || 'Processing...';
    }
  };

  const steps = [
    { threshold: 10, icon: Upload, label: 'Validating file', color: 'brown' },
    { threshold: 30, icon: Upload, label: 'Uploading to cloud', color: 'blue' },
    { threshold: 50, icon: FileSearch, label: 'Extracting text with AI', color: 'purple' },
    { threshold: 80, icon: Sparkles, label: 'Generating summary & definitions', color: 'yellow' },
    { threshold: 100, icon: Save, label: 'Finalizing document', color: 'green' },
  ];

  return (
    <div className="w-full backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl border border-cream-300/50 p-4 sm:p-5 md:p-6 shadow-lg">
      
      {/* Status Icon and Text - Responsive */}
      <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-5 md:mb-6">
        {getStatusIcon()}
        <div className="flex-1">
          <p className="text-sm sm:text-base md:text-lg font-semibold text-brown-900">
            {getStatusText()}
          </p>
          {status === 'processing' && (
            <p className="text-xs sm:text-sm text-brown-600 mt-0.5 sm:mt-1">
              This may take a few moments...
            </p>
          )}
        </div>
        <span className="text-lg sm:text-xl md:text-2xl font-bold text-brown-700">
          {Math.round(progress)}%
        </span>
      </div>

      {/* Progress Bar - Responsive height */}
      <div className="w-full bg-cream-200/50 rounded-full h-2.5 sm:h-3 md:h-3.5 overflow-hidden mb-4 sm:mb-5 md:mb-6">
        <div
          className={`h-full transition-all duration-500 ease-out ${getStatusColor()} rounded-full`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Processing Steps - Responsive text and spacing */}
      {status === 'processing' && (
        <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
          {steps.map((step, index) => {
            const isComplete = progress >= step.threshold;
            const isCurrent = progress >= (steps[index - 1]?.threshold || 0) && progress < step.threshold;
            
            const StepIcon = step.icon;
            
            return (
              <div 
                key={index}
                className={`flex items-center transition-all duration-300 ${
                  isComplete ? 'opacity-100' : isCurrent ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 rounded-full mr-2 sm:mr-3 flex items-center justify-center transition-all ${
                  isComplete 
                    ? 'bg-green-100 border-2 border-green-500' 
                    : isCurrent
                    ? 'bg-brown-100 border-2 border-brown-500'
                    : 'bg-cream-100 border-2 border-cream-300'
                }`}>
                  {isComplete ? (
                    <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-green-600" />
                  ) : isCurrent ? (
                    <StepIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 text-brown-600 animate-pulse" />
                  ) : (
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-cream-300 rounded-full"></div>
                  )}
                </div>
                <span className={`text-xs sm:text-sm md:text-base transition-colors ${
                  isComplete 
                    ? 'text-green-700 font-medium' 
                    : isCurrent
                    ? 'text-brown-900 font-medium'
                    : 'text-brown-600'
                }`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Success Message - Responsive */}
      {status === 'completed' && (
        <div className="p-3 sm:p-4 bg-green-50/50 border border-green-200/50 rounded-lg sm:rounded-xl backdrop-blur-sm">
          <p className="text-xs sm:text-sm md:text-base text-green-800 text-center font-medium">
            🎉 Your document has been processed successfully!
          </p>
        </div>
      )}

      {/* Error Message - Responsive */}
      {status === 'failed' && (
        <div className="p-3 sm:p-4 bg-red-50/50 border border-red-200/50 rounded-lg sm:rounded-xl backdrop-blur-sm">
          <p className="text-xs sm:text-sm md:text-base text-red-800 text-center font-medium">
            ⚠️ Processing failed. Please try again.
          </p>
        </div>
      )}
    </div>
  );
};

export default UploadProgress;